"use client";

import * as React from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { TabsList } from "./tabs";
import { cn } from "../../utils";

export type DialogTabsListProps = React.ComponentPropsWithoutRef<
  typeof TabsPrimitive.List
>;

/**
 * Sticky variant of `TabsList` for use inside the shared `Dialog`. The
 * outer wrapper sticks to the top of the dialog's scrolling content area
 * so the tab bar stays reachable while a long form scrolls underneath.
 *
 * The negative-margin trick (`-mx-4 sm:-mx-6 -mt-4`) cancels the
 * `Dialog`'s default `contentClassName` padding (`px-4 py-4 sm:px-6`) so
 * the sticky bar's `bg-card` spans edge-to-edge while the scrolling form
 * underneath keeps its content inset. The horizontal padding is then
 * re-added inside (`px-4 sm:px-6`) so the tab triggers line up with the
 * form below them.
 *
 * If a Dialog overrides `contentClassName`, the consumer should also
 * override the wrapper margins to match — but the default is what every
 * dialog in the app uses today, and pushing this into a shared component
 * means we don't repeat the four-class incantation at each call site.
 *
 * Inherits `TabsList`'s underline rule so dialog tabs read the same as
 * the platform-wide tabs (active trigger underlined against a thin row
 * border). An earlier `border-b-0` override stripped the row rule and
 * left the active underline floating — visually inconsistent with the
 * rest of the app.
 */
export const DialogTabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  DialogTabsListProps
>(({ className, children, ...props }, ref) => (
  <div className="sticky -top-4 z-10 -mx-4 sm:-mx-6 -mt-4 px-4 sm:px-6 bg-card">
    <TabsList ref={ref} className={cn(className)} {...props}>
      {children}
    </TabsList>
  </div>
));
DialogTabsList.displayName = "DialogTabsList";
